import { productForSale, variantForSale } from '@/api/sale-api';
import { toast } from '@/hooks/use-toast';
import { useBreakpointReady } from '@/hooks/use-breakpoint-ready';
import { useDeviceType } from '@/hooks/use-device-type';
import { getProductLimitByDevice } from '@/lib/utils';
import { TProductWithRelation } from '@/types/product-type';
import { ProductVariantRow } from '@/types/sale-type';
import type { PinnedVariantWithStock } from '@/types/sale-type';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import { SALE_PINS_KEY, saleProductsKey } from '../lib/sale-cache-keys';
import { toPinnedStock } from '../lib/sale-stock-utils';

type ProductForSaleResult = {
  products: TProductWithRelation[];
  total: number;
};

async function fetchProductForSale(params: {
  limitProduct: number;
  offsetProduct: number;
  search: string;
  isPinnedOnly: boolean;
  onlyInStock: boolean;
  orderingColumn: 'stock_in_hand' | 'sold_qty';
  orderingDirection: 'ASC' | 'DESC';
  selectedLetter: string | null;
}): Promise<ProductForSaleResult> {
  const resp = await productForSale(
    params.limitProduct,
    params.offsetProduct,
    params.search,
    params.isPinnedOnly,
    params.onlyInStock,
    params.orderingColumn,
    params.orderingDirection,
    params.selectedLetter,
  );

  if (!resp.success || !resp.data) {
    throw new Error(resp.message || 'Error getting product for sale');
  }

  void globalMutate(SALE_PINS_KEY, resp.data.pinVariants, { revalidate: false });

  return {
    products: resp.data.data,
    total: resp.data.total,
  };
}

export function useSaleProductsData() {
  const { isMobile, ready } = useBreakpointReady();
  const { isTablet } = useDeviceType();

  const [offsetProduct, setOffsetProduct] = useState(0);

  const [search, setSearch] = useState('');
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [orderingColumn, setOrderingColum] = useState<'stock_in_hand' | 'sold_qty'>('sold_qty');
  const [orderingDirection, setOrderingDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [isPinnedOnly, setIsPinnedOnly] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);

  const limitProduct = useMemo(() => {
    if (!ready) {
      if (typeof window !== 'undefined') return getProductLimitByDevice(window.innerWidth);
      return 50;
    }
    if (isMobile) return 6;
    if (isTablet) return 20;
    return 24;
  }, [ready, isMobile, isTablet]);

  useEffect(() => {
    if (!ready) return;
    setOffsetProduct(0);
  }, [limitProduct, ready]);

  useEffect(() => {
    setOffsetProduct(0);
  }, [selectedLetter]);

  const swrKey = ready
    ? saleProductsKey({
        limitProduct,
        offsetProduct,
        search,
        isPinnedOnly,
        onlyInStock,
        orderingColumn,
        orderingDirection,
        selectedLetter,
      })
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    swrKey,
    () =>
      fetchProductForSale({
        limitProduct,
        offsetProduct,
        search,
        isPinnedOnly,
        onlyInStock,
        orderingColumn,
        orderingDirection,
        selectedLetter,
      }),
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
      onError: () => {
        toast({ title: 'Error', description: 'Error getting product for sale', variant: 'destructive' });
      },
    },
  );

  const products = data?.products ?? [];
  const totalProduct = data?.total ?? 0;
  const hasLoadedProducts = !!data;
  const isLoadingProduct = isLoading && !hasLoadedProducts;
  const isRefreshingProduct = isValidating && hasLoadedProducts;

  const refreshSaleProduct = useCallback(() => mutate(), [mutate]);

  const patchProductPinState = useCallback((variantId: string, pinned: boolean) => {
    mutate(
      (current) => {
        if (!current) return current;
        return {
          ...current,
          products: current.products.map((pwr) => ({
            ...pwr,
            productVariants: pwr.productVariants.map((variant) =>
              variant.id === variantId ? { ...variant, pinned } : variant,
            ),
          })),
        };
      },
      { revalidate: false },
    );
  }, [mutate]);

  const loadVariantStock = useCallback(
    async (variantId: string) => {
      const resp = await variantForSale(variantId);
      if (!resp.success) {
        toast({
          title: 'Error',
          description: resp.message || 'Error getting variant for update...',
          variant: 'destructive',
        });
        return;
      }

      const newUpdate = resp.data;
      mutate(
        (current) => {
          if (!current) return current;
          return {
            ...current,
            products: current.products.map((pro) =>
              pro.product.id === newUpdate.product.id ? newUpdate : pro,
            ),
          };
        },
        { revalidate: false },
      );

      void globalMutate(
        SALE_PINS_KEY,
        (currentPins: PinnedVariantWithStock[] | undefined) => {
          if (!currentPins) return currentPins;
          const updateVariantsSet = new Set(newUpdate.productVariants.map((v) => v.id));
          return currentPins.map((pinned) => {
            if (!updateVariantsSet.has(pinned.id)) return pinned;
            const updatedVariant = newUpdate.productVariants.find((uv) => uv.id === pinned.id);
            if (!updatedVariant) return pinned;
            return {
              id: pinned.id,
              product: newUpdate.product,
              variant: updatedVariant,
              ...toPinnedStock(updatedVariant, newUpdate.product),
            };
          });
        },
        { revalidate: false },
      );
    },
    [mutate],
  );

  const rows = useMemo<ProductVariantRow[]>(() => {
    return products.flatMap((pwr) => {
      const product = pwr.product;
      return (pwr.productVariants || []).map((variant, variantIndex) => {
        const variantId = String(variant.id ?? `${product.id}-${variantIndex}`);
        return { p: product, v: variant, key: variantId, variantId };
      });
    });
  }, [products]);

  const onPageChange = useCallback((offset: number) => {
    setOffsetProduct(offset);
  }, []);

  return {
    products,
    totalProduct,
    isLoadingProduct,
    isRefreshingProduct,
    offsetProduct,
    limitProduct,
    search,
    setSearch,
    onlyInStock,
    setOnlyInStock,
    orderingColumn,
    orderingDirection,
    setOrderingColum,
    setOrderingDirection,
    isPinnedOnly,
    setIsPinnedOnly,
    selectedLetter,
    setSelectedLetter,
    rows,
    onPageChange,
    refreshSaleProduct,
    loadVariantStock,
    patchProductPinState,
    error,
  };
}
